import type { PaginationMeta } from "@investhinks/api-client";
import { useCallback, useEffect, useMemo, useState } from "react";

export const defaultPagination: PaginationMeta = {
  page: 1,
  pageSize: 10,
  pageCount: 1,
  total: 0,
};

interface InitialPagination {
  page?: number;
  pageSize?: number;
  pageCount?: number;
  total?: number;
}

interface UsePaginationResult<T> {
  pageItems: T[];
  pagination: PaginationMeta;
  setPagination: React.Dispatch<React.SetStateAction<PaginationMeta>>;
  goToPage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  resetPagination: () => void;
}

const usePagination = <T,>(
  items: T[] | undefined,
  initialPagination: InitialPagination = {}
): UsePaginationResult<T> => {
  const defaultInitialPagination = useMemo(() => ({ ...defaultPagination, ...initialPagination }), [initialPagination]);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultInitialPagination);
  const { pageCount = 1 } = pagination;

  const goToPage = useCallback(
    (page: number) => {
      if (page > 0 && page <= pageCount) setPagination((p) => ({ ...p, page }));
    },
    [pageCount]
  );

  const changePageSize = useCallback((pageSize: number) => {
    if (pageSize > 0) setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const resetPagination = () => setPagination(defaultInitialPagination);

  useEffect(
    () =>
      items &&
      setPagination(({ pageSize = 10, page = 1 }) => {
        const newPageCount = Math.ceil(items.length / pageSize) || 1;
        const newPage = page > newPageCount ? 1 : page;
        return {
          page: newPage,
          pageSize,
          pageCount: newPageCount,
          total: items?.length || 0,
        };
      }),
    [items, pagination.pageSize]
  );

  const pageItems = useMemo(() => {
    const { page = 1, pageSize = 10 } = pagination;
    return items ? items.slice((page - 1) * pageSize, page * pageSize) : [];
  }, [items, pagination]);

  return {
    pageItems,
    pagination,
    setPagination,
    goToPage,
    changePageSize,
    resetPagination,
  };
};

export default usePagination;
